<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useSyncDrainProgress } from '@/composables/useSyncDrainProgress'
import { syncService } from '@/services/syncService'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'SyncProgressBar' })

/**
 * Ambient top-edge sync progress (sync-feedback.md §4.4) — a minimal GitHub/YouTube-style
 * loading bar so users see sync progress WITHOUT opening the Sync status dialog. Offset by
 * the top safe-area inset so it renders below the iOS Dynamic Island / notch instead of
 * being occluded at the physical screen edge. Purely ambient: no pointer events, and the
 * dialog remains the place with the numbers and the phase label.
 */
const authStore = useAuthStore()
const uiStore = useUiStore()
const { drainProgress } = useSyncDrainProgress()

// Hold the bar at 100% for a moment when the drain completes, then fade out —
// a bar that vanishes mid-flight reads as "broken", not "done".
const completing = ref(false)
let completionTimeout: number | null = null
watch(drainProgress, (progress, previous) => {
  if (progress === null && previous !== null && syncService.syncStatus.value !== 'error') {
    completing.value = true
    if (completionTimeout !== null) clearTimeout(completionTimeout)
    completionTimeout = window.setTimeout(() => {
      completing.value = false
      completionTimeout = null
    }, 800)
  }
})

const isVisible = computed(
  () =>
    authStore.isAuthenticated &&
    !uiStore.isLandingPageVisible &&
    (drainProgress.value !== null || completing.value),
)

const widthPercent = computed(() => (completing.value ? 100 : (drainProgress.value?.percent ?? 0)))

// Amber while a retry episode holds the drain, primary while actively syncing.
const fillClass = computed(() =>
  syncService.syncStatus.value === 'error' ? 'bg-amber-500' : 'bg-primary-500',
)
</script>

<template>
  <div
    v-if="isVisible"
    class="pointer-events-none fixed left-0 right-0 h-0.75"
    :style="{ top: 'env(safe-area-inset-top, 0px)', zIndex: Z_INDEX.TEMPORARY_OVERLAY }"
    aria-hidden="true"
    data-testid="sync-progress-bar"
  >
    <div
      :class="['h-full rounded-r-full shadow-sm transition-all duration-500', fillClass]"
      :style="{ width: `${widthPercent}%` }"
    />
  </div>
</template>
